import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";

import { UsersFilter } from "@/components/common/section-components/filters/UsersFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { PaginationComponent } from "@/components/pagination/PaginationComponent";
import { StandardTable } from "@/components/table/StandardTable";
import { CheckboxCheckedChangeDetails, Flex, IconButton, Text, useDisclosure } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { MobileUser, userFilterSchema } from "./schema/users.schema";
import {
  GetMobileUsersProps,
  GetMobileUsersResponse,
} from "@/services/users.service";
import { MUHeaderElements } from "./schema/header-elements";
import { PermissionCode } from "@/constants/permissions";
import { useRoles } from "@/hooks/useRoles";
import {
  clearFiltersHandler,
  fetchMobileUsersHandler,
  handleValidateMobileUser,
  submitFiltersHandler,
} from "./schema/handler-functions";
import { UserMobileActions } from "@/components/common/section-components/action/UserMobileActions";
import { NoData } from "@/components/common/section-components/NoData";
import { usePolling } from "@/hooks/usePolling";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import { Tooltip } from "@/components/ui/tooltip";
import { AppColors } from "@/styles/colors";
import { MotivoBajaUserModal } from "@/components/modal/MotivoBajaUserModal";

export const UsersPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const [currentFilters, setCurrentFilters] = useState<GetMobileUsersProps>({});

  const [loadingMobileUsers, setLoadingMobileUsers] = useState<boolean>(false);
  const [loadingValidateUser, setLoadingValidateUser] =
    useState<boolean>(false);

  const [mobileUsers, setMobileUsers] = useState<GetMobileUsersResponse | null>(
    {
      listado: [],
      totalPages: 1,
      totalRecords: 0,
    },
  );

  const { control, handleSubmit, setValue, getValues, watch } = useForm({
    resolver: zodResolver(userFilterSchema),
  });
  const [selection, setSelection] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const fetchMobileUsers = async (
    filters?: GetMobileUsersProps,
    showLoad = true,
  ) => {
    await fetchMobileUsersHandler({
      setLoadingMobileUsers,
      setMobileUsers,
      filters,
      showLoad,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) fetchMobileUsers();
  }, [isActive]);

  usePolling({
    callback: () => {
      if (!loadingMobileUsers) {
        fetchMobileUsers({ ...currentFilters, "P.Pagina": page }, false);
      }
    },
    delay: 10000,
    isActive,
  });

  const handlePageChange = async (page: number) => {
    setPage(page);
    setCurrentFilters({
      ...currentFilters,
      "P.Pagina": page,
    });
    await fetchMobileUsers({ ...currentFilters, "P.Pagina": page });
  };

  const hasSelection = selection.length > 0;
  const indeterminate =
    hasSelection && selection.length < Number(mobileUsers?.listado?.length);

  const clearFilters = async () => {
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
    });
  };
  const submitFilters = async (event: FieldValues) => {
    setPage(1);
    submitFiltersHandler({
      event,
      fetchMobileUsers,
      setCurrentFilters,
      setFiltersApplied,
      setOpenFilters,
      navigate,
      fetchPermissions,
    });
  };

  const { userPermissions } = useRoles();

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.USERS && p.tipoPermiso === "W",
    ),
  );
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();
  const [selectedToValidate, setSelectedToValidate] = useState<
    MobileUser | undefined
  >();
  const handleCheck = async (
    _value: CheckboxCheckedChangeDetails,
    item: MobileUser,
  ) => {
    handleValidateMobileUser({
      currentFilters: {
        ...currentFilters,
        "P.Pagina": page,
      },
      item,
      fetchMobileUsers,
      setLoadingValidateUser,
      setSelectedToValidate,
      navigate,
      fetchPermissions,
    });
  };

  const watchedValues = watch();
  const isFormEmpty = Object.values(watchedValues).every(
    (val) => val === undefined || val === "",
  );

  const getActionElements = (item: MobileUser) => (
    <UserMobileActions
      handleCheck={handleCheck}
      item={item}
      loadingValidateUser={loadingValidateUser}
      selectedToValidate={selectedToValidate}
    />
  );

  const { open, setOpen } = useDisclosure();
  const [selectedBajaUser, setSelectedBajaUser] = useState<
    MobileUser | undefined
  >();

  const handleViewMotivoBaja = (item: MobileUser) => {
    setSelectedBajaUser(item);
    setOpen(true);
  };

  const getUserStatus = (item: MobileUser) => {
    if (item?.baja) {
      return <Flex alignItems="center" gap="8px">
        Baja
        <Tooltip
          content={<Text color={AppColors.WHITE_SECONDARY} fontSize="14px">Ver motivo de baja</Text>}
          contentProps={{
            css: {
              paddingX: "10px",
              "--tooltip-bg": AppColors.GREEN_PRIMARY,
            },
          }}>
          <IconButton
            aria-label="Ver motivo de baja"
            variant="plain"
            size="xs"
            onClick={() => handleViewMotivoBaja(item)}
            data-testid={`eye-icon-${item?.id}`}
          >
            <Eye cursor="pointer" />
          </IconButton>
        </Tooltip>
      </Flex>
    }

    return item?.validaIdentidad ? "Registrado" : "Pendiente"
  }

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="12px"
      data-testid="users-page"
    >
      <SectionHeader
        showButtons={false}
        title="Usuarios Mobile"
        loading={loadingMobileUsers}
      />

      {/* Filtros */}
      <UsersFilter
        openFilters={openFilters}
        disableFilters={loadingMobileUsers}
        filtersApplied={filtersApplied}
        handleSubmit={handleSubmit}
        control={control}
        setOpenFilters={setOpenFilters}
        submitFilters={submitFilters}
        clearFilters={clearFilters}
        disableClear={isFormEmpty}
      />

      <Flex flexDir="column" paddingX="20px">
        {Boolean(!loadingMobileUsers && !mobileUsers?.listado?.length) && (
          <NoData />
        )}
        {Boolean(!loadingMobileUsers && mobileUsers?.listado?.length) && (
          <>
            <StandardTable
              noSelection
              indeterminate={indeterminate}
              items={
                mobileUsers?.listado.map((el) => ({
                  ...el,
                  estado: getUserStatus(el),
                })) ?? []
              }
              selection={selection}
              setSelection={setSelection}
              headers={MUHeaderElements}
              noActions={!hasWriteAccess}
              actionElements={getActionElements}
            />
            <PaginationComponent
              page={page}
              handlePageChange={handlePageChange}
              pageSize={10}
              count={mobileUsers?.totalRecords ?? 0}
            />
          </>
        )}
      </Flex>

      <MotivoBajaUserModal
        open={open}
        setOpen={setOpen}
        user={selectedBajaUser}
      />
    </Flex>
  );
};
